import { gql, useQuery } from '@apollo/client'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components/macro'

import { analyticsApolloClient, v2AnalyticsApolloClient } from '../../graphql/thegraph/apollo'
import { getV3SwapUsdValue } from './swapValue'

const V3_TOKEN_QUERY = gql`
  query V3TokenDetail($id: ID!) {
    token(id: $id) {
      id
      symbol
      name
      volumeUSD
      feesUSD
      txCount
      totalValueLocked
      totalValueLockedUSD
      derivedETH
      tokenDayData(first: 300, orderBy: date, orderDirection: desc) {
        id
        date
        volumeUSD
        totalValueLockedUSD
        priceUSD
      }
    }
    bundles(first: 1) {
      ethPriceUSD
    }
    tokenHourDatas(first: 24, orderBy: periodStartUnix, orderDirection: desc, where: { token: $id }) {
      id
      periodStartUnix
      volumeUSD
      totalValueLockedUSD
      priceUSD
    }
    token0Swaps: swaps(first: 20, orderBy: timestamp, orderDirection: desc, where: { token0: $id }) {
      id
      timestamp
      origin
      amount0
      amount1
      amountUSD
      transaction {
        id
      }
      token0 {
        symbol
        name
        derivedETH
      }
      token1 {
        symbol
        name
        derivedETH
      }
    }
    token1Swaps: swaps(first: 20, orderBy: timestamp, orderDirection: desc, where: { token1: $id }) {
      id
      timestamp
      origin
      amount0
      amount1
      amountUSD
      transaction {
        id
      }
      token0 {
        symbol
        name
        derivedETH
      }
      token1 {
        symbol
        name
        derivedETH
      }
    }
  }
`

const V2_TOKEN_QUERY = gql`
  query V2TokenDetail($id: ID!) {
    token(id: $id) {
      id
      symbol
      name
      tradeVolumeUSD
      txCount
      totalLiquidity
      derivedETH
      tokenDayData(first: 300, orderBy: date, orderDirection: desc) {
        id
        date
        dailyVolumeUSD
        totalLiquidityUSD
        priceUSD
      }
      pairBase(first: 10) {
        pairHourData(first: 24, orderBy: hourStartUnix, orderDirection: desc) {
          id
          hourStartUnix
          hourlyVolumeUSD
          reserveUSD
        }
        swaps(first: 20, orderBy: timestamp, orderDirection: desc) {
          id
          timestamp
          from
          amount0In
          amount0Out
          amount1In
          amount1Out
          amountUSD
          transaction {
            id
          }
          pair {
            token0 {
              symbol
              name
            }
            token1 {
              symbol
              name
            }
          }
        }
      }
      pairQuote(first: 10) {
        pairHourData(first: 24, orderBy: hourStartUnix, orderDirection: desc) {
          id
          hourStartUnix
          hourlyVolumeUSD
          reserveUSD
        }
        swaps(first: 20, orderBy: timestamp, orderDirection: desc) {
          id
          timestamp
          from
          amount0In
          amount0Out
          amount1In
          amount1Out
          amountUSD
          transaction {
            id
          }
          pair {
            token0 {
              symbol
              name
            }
            token1 {
              symbol
              name
            }
          }
        }
      }
    }
    bundle(id: "1") {
      ethPrice
    }
  }
`

type TokenDay = {
  id: string
  date: number
  volumeUSD?: string
  dailyVolumeUSD?: string
  totalValueLockedUSD?: string
  totalLiquidityUSD?: string
  priceUSD: string
}
type TokenHour = {
  id: string
  periodStartUnix: number
  volumeUSD: string
  totalValueLockedUSD: string
  priceUSD: string
}
type V2PairHour = {
  id: string
  hourStartUnix: number
  hourlyVolumeUSD: string
  reserveUSD: string
}
type TokenSnapshot = {
  id: string
  date: number
  volumeUSD: number
  liquidityUSD: number
  priceUSD: number
}
type ChartRange = '1d' | '1w' | '1y' | 'all'
const CHART_RANGE_DAYS: Record<Exclude<ChartRange, 'all'>, number> = { '1d': 1, '1w': 7, '1y': 365 }
const CHART_RANGE_LABELS: Record<ChartRange, string> = {
  '1d': '1 day',
  '1w': '1 week',
  '1y': '1 year',
  all: 'All-time',
}
type TokenData = {
  id: string
  symbol: string | null
  name: string | null
  txCount: string
  derivedETH: string
  volumeUSD?: string
  tradeVolumeUSD?: string
  feesUSD?: string
  totalValueLocked?: string
  totalValueLockedUSD?: string
  totalLiquidity?: string
  tokenDayData: TokenDay[]
  pairBase?: { swaps: V2RecentSwap[]; pairHourData: V2PairHour[] }[]
  pairQuote?: { swaps: V2RecentSwap[]; pairHourData: V2PairHour[] }[]
}
type SwapToken = { symbol: string | null; name: string | null; derivedETH?: string }
type V3RecentSwap = {
  id: string
  timestamp: string
  origin: string
  amount0: string
  amount1: string
  amountUSD: string
  transaction: { id: string }
  token0: SwapToken
  token1: SwapToken
}
type V2RecentSwap = {
  id: string
  timestamp: string
  from: string
  amount0In: string
  amount0Out: string
  amount1In: string
  amount1Out: string
  amountUSD: string
  transaction: { id: string }
  pair: { token0: SwapToken; token1: SwapToken }
}
type V3TokenQueryData = {
  token: TokenData | null
  bundles: { ethPriceUSD: string }[]
  tokenHourDatas: TokenHour[]
  token0Swaps: V3RecentSwap[]
  token1Swaps: V3RecentSwap[]
}

const Page = styled.main`
  width: 100%;
  max-width: 1120px;
  padding: 68px 16px 48px;
  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    padding: 24px 12px 32px;
  }
`
const Back = styled(Link)`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.accentAction};
  }
`
const Header = styled.header`
  margin: 16px 0 24px;
`
const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 30px;
`
const Detail = styled.div`
  margin-top: 7px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  overflow-wrap: anywhere;
`
const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.md}px`}) {
    grid-template-columns: 1fr;
  }
`
const Card = styled.section`
  padding: 20px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
`
const ChartTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`
const CornerReadout = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  text-align: right;
  white-space: nowrap;

  strong {
    color: ${({ theme }) => theme.textPrimary};
    font-size: 15px;
  }
`
const RangeControls = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 2px;
  width: 75%;
  margin: 12px auto 0;
  padding: 3px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 999px;
`
const RangeButton = styled.button<{ $active: boolean }>`
  flex: 1;
  min-width: 38px;
  padding: 6px 9px;
  color: ${({ $active, theme }) => ($active ? theme.textPrimary : theme.textSecondary)};
  background: ${({ $active, theme }) => ($active ? theme.backgroundModule : 'transparent')};
  border: 0;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color 120ms ease, background-color 120ms ease;

  &:hover {
    color: ${({ theme }) => theme.accentAction};
    background: ${({ theme }) => theme.accentActionSoft};
    filter: brightness(1.1);
  }
`
const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
`
const Tab = styled.button<{ $active: boolean }>`
  padding: 9px 13px;
  color: ${({ $active, theme }) => ($active ? theme.accentAction : theme.textSecondary)};
  background: ${({ $active, theme }) => ($active ? theme.accentActionSoft : theme.backgroundSurface)};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: color 120ms ease, background-color 120ms ease, border-color 120ms ease;

  &:hover {
    color: ${({ theme }) => theme.accentAction};
    background: ${({ theme }) => theme.accentActionSoft};
    border-color: ${({ theme }) => theme.accentAction};
    filter: brightness(1.1);
  }
`
const Price = styled.div`
  color: ${({ theme }) => theme.textPrimary};
  font-size: 30px;
  font-weight: 600;
`
const Caption = styled.div`
  margin: 7px 0 18px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
`
const Chart = styled.div<{ $count: number }>`
  display: flex;
  align-items: flex-end;
  gap: ${({ $count }) => ($count > 180 ? '0' : $count > 60 ? '1px' : '5px')};
  height: 240px;
  padding-top: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};
  min-width: 0;
  overflow: hidden;
`
const LineChart = styled.svg`
  width: 100%;
  height: 240px;
  padding-top: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};

  polyline {
    fill: none;
    stroke: #d86624;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 3;
  }
`
const PriceChart = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 72px;
  min-height: 240px;
`
const PriceCanvas = styled.div`
  position: relative;
  min-width: 0;
`
const PriceAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 0 22px 12px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
  text-align: right;
`
const HoverLine = styled.div<{ $left: number }>`
  position: absolute;
  top: 8px;
  bottom: 22px;
  left: ${({ $left }) => `${$left}%`};
  width: 1px;
  background: ${({ theme }) => theme.backgroundOutline};
  pointer-events: none;
`
const HoverHorizontal = styled.div<{ $top: number }>`
  position: absolute;
  top: ${({ $top }) => `${$top}%`};
  right: 0;
  left: 0;
  height: 1px;
  background: ${({ theme }) => theme.backgroundOutline};
  pointer-events: none;
`
const HoverPoint = styled.div<{ $left: number; $top: number }>`
  position: absolute;
  top: ${({ $top }) => `${$top}%`};
  left: ${({ $left }) => `${$left}%`};
  width: 10px;
  height: 10px;
  background: #d86624;
  border: 2px solid ${({ theme }) => theme.backgroundSurface};
  border-radius: 50%;
  box-shadow: 0 0 0 1px #d86624;
  pointer-events: none;
  transform: translate(-50%, -50%);
`
const HoverTip = styled.div<{ $left: number }>`
  position: absolute;
  top: 12px;
  left: ${({ $left }) => `${$left}%`};
  z-index: 1;
  padding: 6px 8px;
  color: ${({ theme }) => theme.textPrimary};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px;
  font-size: 12px;
  pointer-events: none;
  transform: translateX(-50%);
  white-space: nowrap;
`
const DateLabels = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
`
const PriceChange = styled.span<{ $positive: boolean }>`
  margin-left: 8px;
  color: ${({ $positive, theme }) => ($positive ? theme.accentSuccess : theme.accentFailure)};
  font-size: 14px;
`
const Bar = styled.button<{ $height: number; $tone: string }>`
  flex: 1;
  min-width: 0;
  height: ${({ $height }) => `${Math.max($height, 3)}%`};
  border-radius: 4px 4px 0 0;
  background: ${({ $tone }) => ($tone === 'volume' ? '#D86624' : $tone === 'liquidity' ? '#D86624' : '#D86624')};
  border: 0;
  padding: 0;
  cursor: crosshair;
  transition: opacity 100ms ease;

  &:hover,
  &:focus-visible {
    background: rgba(246, 160, 79, 0.55);
    outline: none;
  }
`
const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
`
const Stats = styled.aside`
  display: grid;
  gap: 12px;
  align-content: start;
`
const Stat = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 14px;
`
const TransactionsSection = styled.section`
  margin-top: 24px;
`
const TransactionsTitle = styled.h2`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 21px;
`
const TransactionsCard = styled.div`
  overflow-x: auto;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
`
const TransactionsTable = styled.table`
  width: 100%;
  min-width: 680px;
  border-collapse: collapse;

  th,
  td {
    padding: 13px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};
    color: ${({ theme }) => theme.textPrimary};
    font-size: 13px;
    text-align: left;
  }

  th {
    color: ${({ theme }) => theme.textSecondary};
    font-weight: 500;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  tbody tr {
    transition: background 100ms ease;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.accentActionSoft};
  }
`
const TransactionLink = styled.a`
  color: ${({ theme }) => theme.accentAction};
  text-decoration: none;
`
const Label = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
`
const Value = styled.div`
  margin-top: 6px;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 21px;
  font-weight: 600;
`
const Notice = styled.div`
  padding: 18px;
  color: ${({ theme }) => theme.textSecondary};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
`
const fmt = (value: string | number | undefined, usd = false) => {
  const number = Number(value)
  if (!Number.isFinite(number) || number === 0) return '-'
  if (usd && number < 0.01) return '<$0.01'
  return `${usd ? '$' : ''}${number.toLocaleString(undefined, { maximumSignificantDigits: 6 })}`
}
const swapTokenLabel = (token: SwapToken) => token.symbol || token.name || 'Token'
const shortAddress = (value: string) => `${value.slice(0, 6)}…${value.slice(-4)}`
const formatChartDate = (timestamp: number, hourly = false) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(hourly ? { hour: 'numeric' } : {}),
  }).format(new Date(timestamp * 1000))

export default function AnalyticsTokenDetail() {
  const { protocol, tokenId } = useParams<{ protocol: 'v2' | 'v3'; tokenId: string }>()
  const v3 = protocol === 'v3'
  const [tab, setTab] = useState<'price' | 'volume' | 'liquidity'>('price')
  const [chartRange, setChartRange] = useState<ChartRange>('1w')
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const {
    data: v3Data,
    loading: v3Loading,
    error: v3Error,
  } = useQuery<V3TokenQueryData>(V3_TOKEN_QUERY, {
    client: analyticsApolloClient,
    variables: { id: tokenId?.toLowerCase() ?? '' },
    skip: !v3,
  })
  const {
    data: v2Data,
    loading: v2Loading,
    error: v2Error,
  } = useQuery<{ token: TokenData | null; bundle: { ethPrice: string } | null }>(V2_TOKEN_QUERY, {
    client: v2AnalyticsApolloClient,
    variables: { id: tokenId?.toLowerCase() ?? '' },
    skip: v3,
  })
  const token = v3 ? v3Data?.token : v2Data?.token
  const wonePrice = v3 ? Number(v3Data?.bundles[0]?.ethPriceUSD) : Number(v2Data?.bundle?.ethPrice)
  const price = Number(token?.derivedETH) * wonePrice
  const liquidity = token?.totalValueLockedUSD ?? (Number(token?.totalLiquidity) * price).toString()
  const allDays: TokenSnapshot[] = [...(token?.tokenDayData ?? [])].reverse().map((day) => ({
    id: day.id,
    date: day.date,
    volumeUSD: Number(day.volumeUSD ?? day.dailyVolumeUSD),
    liquidityUSD: Number(day.totalValueLockedUSD ?? day.totalLiquidityUSD),
    priceUSD: Number(day.priceUSD),
  }))
  const latestHour = Math.floor(Date.now() / 3_600_000) * 3_600
  const firstHour = latestHour - 23 * 3_600
  const hourlyDays: TokenSnapshot[] = Array.from({ length: 24 }, (_, index) => ({
    id: `hour-${firstHour + index * 3_600}`,
    date: firstHour + index * 3_600,
    volumeUSD: 0,
    liquidityUSD: Number(liquidity),
    priceUSD: price,
  }))
  if (v3) {
    for (const hour of v3Data?.tokenHourDatas ?? []) {
      const bucket = Math.floor((hour.periodStartUnix - firstHour) / 3_600)
      if (bucket >= 0 && bucket < hourlyDays.length) {
        hourlyDays[bucket] = {
          id: hour.id,
          date: hour.periodStartUnix,
          volumeUSD: Number(hour.volumeUSD),
          liquidityUSD: Number(hour.totalValueLockedUSD),
          priceUSD: Number(hour.priceUSD),
        }
      }
    }
  } else {
    const hourlyLiquidity = new Map<number, number>()
    for (const pair of [...(token?.pairBase ?? []), ...(token?.pairQuote ?? [])]) {
      for (const hour of pair.pairHourData) {
        const bucket = Math.floor((hour.hourStartUnix - firstHour) / 3_600)
        if (bucket < 0 || bucket >= hourlyDays.length) continue
        hourlyDays[bucket].volumeUSD += Number(hour.hourlyVolumeUSD)
        hourlyLiquidity.set(bucket, (hourlyLiquidity.get(bucket) ?? 0) + Number(hour.reserveUSD) / 2)
      }
    }
    for (const [bucket, value] of hourlyLiquidity) hourlyDays[bucket].liquidityUSD = value
  }
  const useHourlyDays = chartRange === '1d' && (tab === 'volume' || tab === 'liquidity')
  const days = useHourlyDays
    ? hourlyDays
    : chartRange === 'all'
    ? allDays
    : allDays.slice(-CHART_RANGE_DAYS[chartRange])
  const values = days.map((day) =>
    tab === 'price' ? day.priceUSD : tab === 'volume' ? day.volumeUSD : day.liquidityUSD
  )
  const maximum = Math.max(...values, 0)
  const monthlyVolume = days.reduce((total, day) => total + day.volumeUSD, 0)
  const firstPrice = values.find((value) => Number.isFinite(value) && value > 0)
  const latestPrice = [...values].reverse().find((value) => Number.isFinite(value) && value > 0)
  const monthlyPriceChange = firstPrice && latestPrice ? ((latestPrice - firstPrice) / firstPrice) * 100 : undefined
  const linePoints = values
    .map(
      (value, index) =>
        `${values.length === 1 ? 50 : (index / (values.length - 1)) * 100},${
          100 - (maximum > 0 ? (value / maximum) * 92 + 4 : 50)
        }`
    )
    .join(' ')
  const areaPoints = `${values.length === 1 ? 50 : 0},100 ${linePoints} ${values.length === 1 ? 50 : 100},100`
  const minimumPrice = Math.min(...values.filter(Number.isFinite))
  const maximumPrice = Math.max(...values.filter(Number.isFinite))
  const priceRange = maximumPrice - minimumPrice || Math.max(maximumPrice * 0.02, 1)
  const priceAxisValues = Array.from(
    { length: 6 },
    (_, index) => maximumPrice + priceRange * 0.08 - ((priceRange * 1.16) / 5) * index
  )
  const hoverValue = hoverIndex === null ? undefined : values[hoverIndex]
  const hoverDate = hoverIndex === null ? undefined : days[hoverIndex]?.date
  const readoutIndex = hoverIndex ?? days.length - 1
  const readoutValue = values[readoutIndex]
  const readoutDate = days[readoutIndex]?.date
  const showCornerReadout = tab === 'volume' || (!v3 && tab === 'liquidity')
  const periodLabel = CHART_RANGE_LABELS[chartRange]
  const recentSwaps = v3
    ? [...(v3Data?.token0Swaps ?? []), ...(v3Data?.token1Swaps ?? [])]
        .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
        .filter((swap, index, swaps) => swaps.findIndex((candidate) => candidate.id === swap.id) === index)
        .slice(0, 20)
    : [...(token?.pairBase ?? []), ...(token?.pairQuote ?? [])]
        .flatMap((pair) => pair.swaps)
        .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
        .filter((swap, index, swaps) => swaps.findIndex((candidate) => candidate.id === swap.id) === index)
        .slice(0, 20)
  const dateLabelIndexes = Array.from(
    new Set([
      0,
      Math.floor((days.length - 1) / 4),
      Math.floor((days.length - 1) / 2),
      Math.floor(((days.length - 1) * 3) / 4),
      days.length - 1,
    ])
  ).filter((index) => index >= 0)
  if (!tokenId || (protocol !== 'v2' && protocol !== 'v3'))
    return (
      <Page>
        <Notice>Choose a valid V2 or V3 token from Analytics.</Notice>
      </Page>
    )
  if (v3 ? v3Loading : v2Loading)
    return (
      <Page>
        <Notice>Loading token analytics…</Notice>
      </Page>
    )
  if (!token)
    return (
      <Page>
        <Back to="/analytics">← Analytics</Back>
        <Notice>Unable to load this token. {(v3 ? v3Error : v2Error)?.message ?? 'The token is not indexed.'}</Notice>
      </Page>
    )
  return (
    <Page>
      <Back to="/analytics">← Analytics</Back>
      <Header>
        <Title>{token.symbol || token.name || 'Token'}</Title>
        <Detail>
          {v3 ? 'V3 token' : 'V2 token'} · {token.id}
        </Detail>
      </Header>
      <Layout>
        <Card>
          <Tabs role="tablist" aria-label="Token analytics chart">
            {(['price', 'volume', 'liquidity'] as const).map((item) => (
              <Tab key={item} type="button" role="tab" $active={tab === item} onClick={() => setTab(item)}>
                {item[0].toUpperCase() + item.slice(1)}
              </Tab>
            ))}
          </Tabs>
          <ChartTop>
            <Price>
              {tab === 'price' ? fmt(price, true) : tab === 'volume' ? fmt(monthlyVolume, true) : fmt(liquidity, true)}
            </Price>
            {showCornerReadout && readoutDate && readoutValue !== undefined && (
              <CornerReadout>
                {formatChartDate(readoutDate, useHourlyDays)}: <strong>{fmt(readoutValue, true)}</strong>
              </CornerReadout>
            )}
          </ChartTop>
          {tab === 'price' && monthlyPriceChange !== undefined && (
            <PriceChange $positive={monthlyPriceChange >= 0}>
              {monthlyPriceChange >= 0 ? '▲' : '▼'} {Math.abs(monthlyPriceChange).toFixed(2)}% past month
            </PriceChange>
          )}
          <Caption>
            {tab === 'price'
              ? `${token.symbol || 'Token'} price`
              : tab === 'volume'
              ? `${periodLabel} indexed volume`
              : 'Current liquidity'}{' '}
            · {chartRange === 'all' ? 'all indexed days' : chartRange.toUpperCase()}
          </Caption>
          {tab === 'price' ? (
            <PriceChart>
              <PriceCanvas
                onMouseLeave={() => setHoverIndex(null)}
                onMouseMove={(event) => {
                  const bounds = event.currentTarget.getBoundingClientRect()
                  const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width))
                  setHoverIndex(Math.round(ratio * Math.max(values.length - 1, 0)))
                }}
              >
                <LineChart viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Token price history">
                  <defs>
                    <linearGradient id="token-price-area" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F6A04F" stopOpacity="0.99" />
                      <stop offset="100%" stopColor="#F6A04F" stopOpacity="0.01" />
                    </linearGradient>
                  </defs>
                  <polygon points={areaPoints} fill="url(#token-price-area)" />
                  <polyline points={linePoints} vectorEffect="non-scaling-stroke" />
                </LineChart>
                {hoverIndex !== null && hoverValue !== undefined && (
                  <>
                    <HoverLine $left={(hoverIndex / Math.max(values.length - 1, 1)) * 100} />
                    <HoverHorizontal $top={100 - (maximum > 0 ? (hoverValue / maximum) * 92 + 4 : 50)} />
                    <HoverPoint
                      $left={(hoverIndex / Math.max(values.length - 1, 1)) * 100}
                      $top={100 - (maximum > 0 ? (hoverValue / maximum) * 92 + 4 : 50)}
                    />
                    <HoverTip $left={(hoverIndex / Math.max(values.length - 1, 1)) * 100}>
                      {hoverDate ? formatChartDate(hoverDate, useHourlyDays) : 'Current'}:{' '}
                      <strong>{fmt(hoverValue, true)}</strong>
                    </HoverTip>
                  </>
                )}
              </PriceCanvas>
              <PriceAxis>
                {priceAxisValues.map((axisValue, index) => (
                  <span key={index}>{fmt(axisValue, true)}</span>
                ))}
              </PriceAxis>
            </PriceChart>
          ) : (
            <Chart $count={values.length} onMouseLeave={() => setHoverIndex(null)}>
              {values.length ? (
                values.map((value, index) => (
                  <Bar
                    key={days[index].id}
                    type="button"
                    $tone={tab}
                    $height={maximum > 0 ? (value / maximum) * 100 : 0}
                    title={fmt(value, tab === 'volume')}
                    aria-label={`${formatChartDate(days[index].date, useHourlyDays)}: ${fmt(value, tab === 'volume')}`}
                    onMouseEnter={() => setHoverIndex(index)}
                    onFocus={() => setHoverIndex(index)}
                  />
                ))
              ) : (
                <Notice>No historical token data has been indexed yet.</Notice>
              )}
            </Chart>
          )}
          {tab === 'price' ? (
            <DateLabels>
              {dateLabelIndexes.map((index) => (
                <span key={index}>{days[index] ? formatChartDate(days[index].date, useHourlyDays) : 'Current'}</span>
              ))}
            </DateLabels>
          ) : (
            <Foot>
              <span>{days[0] ? formatChartDate(days[0].date, useHourlyDays) : 'Current'}</span>
              <span>
                {days[days.length - 1] ? formatChartDate(days[days.length - 1].date, useHourlyDays) : 'Current'}
              </span>
            </Foot>
          )}
          <RangeControls role="tablist" aria-label="Chart time range">
            {(['1d', '1w', '1y', 'all'] as const).map((range) => (
              <RangeButton
                key={range}
                type="button"
                role="tab"
                aria-selected={chartRange === range}
                $active={chartRange === range}
                onClick={() => setChartRange(range)}
              >
                {range === 'all' ? 'All' : range.toUpperCase()}
              </RangeButton>
            ))}
          </RangeControls>
        </Card>
        <Stats>
          <Stat>
            <Label>Current price</Label>
            <Value>{fmt(price, true)}</Value>
            <Detail>Derived from the indexed WONE price.</Detail>
          </Stat>
          <Stat>
            <Label>{periodLabel} volume</Label>
            <Value>{fmt(monthlyVolume, true)}</Value>
          </Stat>
          <Stat>
            <Label>Liquidity</Label>
            <Value>{fmt(liquidity, true)}</Value>
          </Stat>
          <Stat>
            <Label>Transactions</Label>
            <Value>{fmt(token.txCount)}</Value>
          </Stat>
          {v3 && (
            <Stat>
              <Label>All-time fees</Label>
              <Value>{fmt(token.feesUSD, true)}</Value>
            </Stat>
          )}
          <Stat>
            <Label>Token liquidity</Label>
            <Value>{fmt(token.totalValueLocked ?? token.totalLiquidity)}</Value>
          </Stat>
        </Stats>
      </Layout>
      <TransactionsSection>
        <TransactionsTitle>Recent transactions</TransactionsTitle>
        <TransactionsCard>
          <TransactionsTable>
            <thead>
              <tr>
                <th>Swap</th>
                <th>Value</th>
                <th>Account</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {recentSwaps.map((swap) => {
                const v3Swap = 'amount0' in swap
                const token0Input = v3Swap ? Number(swap.amount0) > 0 : Number(swap.amount0In) > 0
                const token0 = v3Swap ? swap.token0 : swap.pair.token0
                const token1 = v3Swap ? swap.token1 : swap.pair.token1
                const inputAmount = Math.abs(
                  Number(
                    v3Swap ? (token0Input ? swap.amount0 : swap.amount1) : token0Input ? swap.amount0In : swap.amount1In
                  )
                )
                const outputAmount = Math.abs(
                  Number(
                    v3Swap
                      ? token0Input
                        ? swap.amount1
                        : swap.amount0
                      : token0Input
                      ? swap.amount1Out
                      : swap.amount0Out
                  )
                )
                const account = v3Swap ? swap.origin : swap.from
                const swapValueUSD = v3Swap ? getV3SwapUsdValue(swap, wonePrice) : Math.abs(Number(swap.amountUSD))
                return (
                  <tr key={swap.id}>
                    <td>
                      <TransactionLink
                        href={`https://explorer.harmony.one/tx/${swap.transaction.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {fmt(inputAmount)} {swapTokenLabel(token0Input ? token0 : token1)} → {fmt(outputAmount)}{' '}
                        {swapTokenLabel(token0Input ? token1 : token0)}
                      </TransactionLink>
                    </td>
                    <td>{fmt(swapValueUSD, true)}</td>
                    <td>{shortAddress(account)}</td>
                    <td>{new Date(Number(swap.timestamp) * 1000).toLocaleString()}</td>
                  </tr>
                )
              })}
              {!recentSwaps.length && (
                <tr>
                  <td colSpan={4}>No recent swaps indexed.</td>
                </tr>
              )}
            </tbody>
          </TransactionsTable>
        </TransactionsCard>
      </TransactionsSection>
    </Page>
  )
}
